import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { calendarApi, type CalendarStatus } from '../../../api';

// Calendar sync — Google + Microsoft 365 (OAuth) plus CalDAV (basic auth).
// All three follow the same UX: a per-provider card with a connect / sync /
// disconnect set, plus an inline form for CalDAV credentials.

export default function CalendarSettings() {
  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  // OAuth callbacks bounce back here with ?connected=… or ?error=…
  useEffect(() => {
    const connected = params.get('connected');
    if (connected === '1' || connected === 'google') {
      setToast('Google Calendar connected. Initial sync will run on the next cron tick.');
    } else if (connected === 'microsoft') {
      setToast('Microsoft 365 calendar connected. Initial sync will run on the next cron tick.');
    }
    if (params.get('error')) {
      setError(`Callback: ${params.get('error')}`);
    }
    if (connected || params.get('error')) {
      params.delete('connected');
      params.delete('error');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const load = async () => {
    try {
      const s = await calendarApi.status();
      setStatus(s);
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { load(); }, []);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setError(null);
    try { await fn(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); await load(); }
  };

  const connectGoogle    = () => run('connect-google',    async () => { const r = await calendarApi.googleAuthUrl();    window.open(r.url, '_blank', 'noopener,noreferrer'); });
  const disconnectGoogle = () => {
    if (!confirm('Disconnect Google Calendar? Future diary events will stop pushing to Google.')) return;
    run('disc-google', async () => { await calendarApi.googleDisconnect(); setToast('Google Calendar disconnected.'); });
  };
  const syncNowGoogle    = () => run('sync-google', async () => {
    const r = await calendarApi.googleSyncNow();
    if (r.token_expired) setToast('Sync token had expired — re-armed for next tick.');
    else if (r.pulled != null) setToast(`Pulled ${r.pulled} Google event(s).`);
    else setToast('Sync skipped (not connected).');
  });

  const connectMs    = () => run('connect-ms',    async () => { const r = await calendarApi.microsoftAuthUrl(); window.open(r.url, '_blank', 'noopener,noreferrer'); });
  const disconnectMs = () => {
    if (!confirm('Disconnect Microsoft 365 calendar?')) return;
    run('disc-ms', async () => { await calendarApi.microsoftDisconnect(); setToast('Microsoft 365 disconnected.'); });
  };
  const syncNowMs    = () => run('sync-ms', async () => {
    const r = await calendarApi.microsoftSyncNow();
    if (r.token_expired) setToast('Microsoft delta token had expired — re-armed for next tick.');
    else if (r.pulled != null) setToast(`Pulled ${r.pulled} Microsoft event(s).`);
    else setToast('Sync skipped (not connected).');
  });

  const disconnectCaldav = () => {
    if (!confirm('Disconnect CalDAV server?')) return;
    run('disc-cd', async () => { await calendarApi.caldavDisconnect(); setToast('CalDAV disconnected.'); });
  };
  const testCaldav = () => run('test-cd', async () => {
    const r = await calendarApi.caldavTest();
    if (r.ok) setToast('CalDAV server reachable. Outbound push is live.');
    else setError(`CalDAV test failed: ${r.error || `HTTP ${r.status || '???'}`}`);
  });

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <header>
        <h1>Calendar sync</h1>
        <p className="muted">
          Mirror your Litigate diary into your existing calendar. New diary events push outbound on creation;
          a daily cron pulls events you create elsewhere back into the diary view.
        </p>
      </header>

      {toast && <div className="card" style={{ borderColor: 'var(--color-accent)', background: 'rgba(201,163,74,0.08)' }}>{toast}</div>}
      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      {status === null ? (
        <div className="card muted">Loading…</div>
      ) : (
        <>
          {/* --- Google Calendar --- */}
          <ProviderCard
            title="Google Calendar"
            status={status.google_calendar}
            envHint={<>Set <code>GOOGLE_CLIENT_ID</code> + <code>GOOGLE_CLIENT_SECRET</code> on the api function then redeploy.</>}
            connectLabel="Connect Google"
            onConnect={connectGoogle}
            onDisconnect={disconnectGoogle}
            onSyncNow={syncNowGoogle}
            busyKey={busy}
            keys={{ connect: 'connect-google', disc: 'disc-google', sync: 'sync-google' }}
          />

          {/* --- Microsoft 365 --- */}
          <ProviderCard
            title="Microsoft 365 calendar"
            status={status.microsoft_calendar}
            envHint={<>Set <code>MS_CLIENT_ID</code> + <code>MS_CLIENT_SECRET</code> on the api function then redeploy. Use a Microsoft Entra app registration with the <code>Calendars.ReadWrite</code> + <code>offline_access</code> delegated scopes and a redirect URI of <code>{`${window.location.origin}/server/api/calendars/microsoft/callback`}</code>.</>}
            connectLabel="Connect Microsoft"
            onConnect={connectMs}
            onDisconnect={disconnectMs}
            onSyncNow={syncNowMs}
            busyKey={busy}
            keys={{ connect: 'connect-ms', disc: 'disc-ms', sync: 'sync-ms' }}
          />

          {/* --- CalDAV --- */}
          <CalDavCard
            status={status.caldav}
            busyKey={busy}
            onConnected={() => { setToast('CalDAV connected. Outbound push is live for new diary events.'); load(); }}
            onError={(e) => setError(e)}
            onDisconnect={disconnectCaldav}
            onTest={testCaldav}
            setBusy={setBusy}
          />
        </>
      )}
    </div>
  );
}

type Keys = { connect: string; disc: string; sync: string };
function ProviderCard({
  title, status, envHint, connectLabel, onConnect, onDisconnect, onSyncNow, busyKey, keys,
}: {
  title: string;
  status: { configured: boolean; connected: boolean; calendar_id?: string; last_sync_at?: string | null; last_sync_status?: string | null; last_sync_error?: string | null };
  envHint: React.ReactNode;
  connectLabel: string;
  onConnect: () => void;
  onDisconnect: () => void;
  onSyncNow: () => void;
  busyKey: string | null;
  keys: Keys;
}) {
  return (
    <div className="card stack">
      <div className="row">
        <div className="grow">
          <h3 style={{ margin: 0 }}>{title}</h3>
          {!status.configured ? (
            <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
              Not configured on this deployment. {envHint}
            </p>
          ) : !status.connected ? (
            <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
              Connect to push diary events into your {title} and pull external events back here.
            </p>
          ) : (
            <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
              Connected{status.calendar_id ? ` to ${status.calendar_id}` : ''}.
              Last sync: {status.last_sync_at
                ? `${String(status.last_sync_at).slice(0, 16).replace('T', ' ')} (${status.last_sync_status})`
                : 'never'}.
              {status.last_sync_error && (
                <span style={{ color: 'var(--color-danger)' }}> {status.last_sync_error}</span>
              )}
            </p>
          )}
        </div>
        <div className="row" style={{ gap: 8 }}>
          {status.connected ? (
            <>
              <button onClick={onSyncNow} disabled={busyKey === keys.sync}>
                {busyKey === keys.sync ? '…' : 'Sync now'}
              </button>
              <button onClick={onDisconnect} disabled={busyKey === keys.disc}>
                {busyKey === keys.disc ? '…' : 'Disconnect'}
              </button>
            </>
          ) : (
            <button
              className="primary"
              onClick={onConnect}
              disabled={!status.configured || busyKey === keys.connect}
            >
              {busyKey === keys.connect ? '…' : connectLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CalDavCard({
  status, busyKey, onConnected, onError, onDisconnect, onTest, setBusy,
}: {
  status: { configured: boolean; connected: boolean; calendar_url?: string; username?: string; last_sync_at?: string | null; last_sync_status?: string | null; last_sync_error?: string | null };
  busyKey: string | null;
  onConnected: () => void;
  onError: (e: string) => void;
  onDisconnect: () => void;
  onTest: () => void;
  setBusy: (k: string | null) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [calUrl, setCalUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');

  const submit = async () => {
    setBusy('cd-connect');
    try {
      await calendarApi.caldavConnect({
        calendar_url: calUrl.trim(),
        username: username.trim(),
        app_password: appPassword,
      });
      setShowForm(false);
      setCalUrl(''); setUsername(''); setAppPassword('');
      onConnected();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  return (
    <div className="card stack">
      <div className="row">
        <div className="grow">
          <h3 style={{ margin: 0 }}>CalDAV (Apple Calendar / Fastmail / generic)</h3>
          {status.connected ? (
            <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
              Connected as <strong>{status.username || '?'}</strong> against {status.calendar_url}.
              Outbound push is live; inbound pull is not yet implemented for CalDAV.
            </p>
          ) : (
            <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
              Push your Litigate diary to any CalDAV-compatible server. Use an app-specific password
              (iCloud → "App-Specific Passwords"; Fastmail → "App passwords"; Nextcloud → account
              password if WebDAV is on).
            </p>
          )}
        </div>
        <div className="row" style={{ gap: 8 }}>
          {status.connected ? (
            <>
              <button onClick={onTest} disabled={busyKey === 'test-cd'}>
                {busyKey === 'test-cd' ? '…' : 'Test'}
              </button>
              <button onClick={onDisconnect} disabled={busyKey === 'disc-cd'}>
                {busyKey === 'disc-cd' ? '…' : 'Disconnect'}
              </button>
            </>
          ) : (
            <button className="primary" onClick={() => setShowForm((s) => !s)}>
              {showForm ? 'Cancel' : 'Connect CalDAV'}
            </button>
          )}
        </div>
      </div>

      {showForm && !status.connected && (
        <div className="stack" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
          <div>
            <label>Calendar collection URL</label>
            <input
              placeholder="https://caldav.icloud.com/123456789/calendars/home/"
              value={calUrl}
              onChange={(e) => setCalUrl(e.target.value)}
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Apple iCloud uses URLs like <code>https://caldav.icloud.com/&lt;id&gt;/calendars/home/</code>.
              Fastmail uses <code>https://caldav.fastmail.com/dav/calendars/user/&lt;you&gt;/&lt;cal&gt;/</code>.
            </div>
          </div>
          <div className="row">
            <div className="grow">
              <label>Username</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="you@example.com" />
            </div>
            <div className="grow">
              <label>App-specific password</label>
              <input type="password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} />
            </div>
          </div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              className="primary"
              onClick={submit}
              disabled={busyKey === 'cd-connect' || !calUrl || !username || !appPassword}
            >
              {busyKey === 'cd-connect' ? '…' : 'Test & save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
